import React, { useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, BookOpen, CheckCircle2, ChevronDown, ChevronRight, Clock, Download, ExternalLink, Inbox, Info,
    Link2, Loader2, Lock, Mail, Plus, RefreshCw, Scale, Search, Send, Trash2, Upload,
} from 'lucide-react';
import { ART_TITEL, GRUNDSATZ_ART_C, LIEFERANT_TITEL } from '../../services/mitgeltendeDokumente';
import { describeError } from '../../services/errorMessage';
import { fetchEmpfaenger } from '../../services/planungsphaseService';
import { dokumentAnsehen } from '../dokumente/dokumentDatei';
import type { ProjektDokument } from '../dokumente/types';
import { useDokumente } from '../dokumente/useDokumente';
import { AnforderungVersandDialog, type EmpfaengerVorbelegung, type VersandEingabe } from './AnforderungVersandDialog';
import { ADRESSAT_LABEL } from './fragenkatalog';
import { HandbuchImportDialog } from './HandbuchImportDialog';
import { pdfAlsBlob, speicherePdf, type PdfKontext } from './planungIntegration';
import { heuteLokalIso } from './planungLogic';
import { versende } from './planungVersand';
import { rueckflussVorschlaege, type BelegVorschlag } from './rueckflussLogic';
import { anforderungDateiname, baueUnterlagenAnforderungPdf } from './unterlagenAnforderungPdf';
import {
    baueHandbuchModell,
    handbuchDateiname,
    pruefeHandbuch,
    pruefeHandbuchDatei,
    uebernehmeHandbuchNotizen,
    vergleicheErneut,
    vergleicheHandbuchNotizen,
    type HandbuchGelesen,
    type NotizAktion,
    type NotizVorschlag,
} from './unterlagenHandbuch';
import { baueHandbuchPdf, liesHandbuchPdf, speichereHandbuch } from './unterlagenHandbuchPdf';
import {
    ART_KURZ,
    BELEG_LABEL,
    UNTERLAGEN_STATUS_LABEL,
    UNTERLAGEN_STATUS_WERTE,
    anforderungFassungRef,
    artCDokumente,
    baueUnterlagenZeilen,
    belegVerwaist,
    belegfaehigeDokumente,
    berechneUnterlagenFortschritt,
    datumDeutsch,
    eintragFuerZeile,
    empfaengerVorbelegung,
    entferneEigeneUnterlage,
    filtereUnterlagen,
    fortschrittText,
    fuehreAnforderungVersandAus,
    gruppiereUnterlagen,
    lieferantenUebersicht,
    markiereAlsAngefordert,
    neueEigeneUnterlage,
    relevanzGruende,
    setzeUnterlagenBemerkung,
    setzeUnterlagenFrist,
    setzeUnterlagenStatus,
    ueberfaelligeUnterlagen,
    uebernehmeUnterlage,
    unterlagenFristText,
    unterlagenFristUeberfaellig,
    unterlagenFuerLieferant,
    unterlagenSpeicherFehler,
    verknuepfeUnterlagenDokument,
    vorgabeUnterlagenFrist,
    type StempelErgebnis,
    type UnterlagenFilter,
    type UnterlagenZeile,
} from './unterlagenLogic';
import { useSigePlanDokumente, type AenderungsQuittung, type Speicherstand } from './useUnterlagen';
import type { FrageAdressat, PlanungUnterlage, ProjektMerkmale, UnterlagenStatus } from './types';

interface Props {
    projectId: string;
    /** Id der Planungsphase — steht im Handbuch und muss beim Einlesen passen. */
    phaseId: string | null;
    /** Wer angemeldet ist — steht im Handbuch unter „Aufgestellt". */
    bearbeiter?: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /** Firmenstammdaten und Briefkopf fuer das Anforderungsblatt. */
    pdfKontext: PdfKontext;
    /** Merkmale aus dem Entwurf der Grundlagen (nach dem Speichern = gespeicherter Stand). */
    merkmale: ProjektMerkmale;
    /** Die Grundlagen haben ungespeicherte Aenderungen. */
    merkmaleUngespeichert: boolean;
    eintraege: PlanungUnterlage[];
    speicherstand: Speicherstand;
    speicherfehler: string | null;
    hinweis: string | null;
    /** Nur lesend: Lesemodus, abgeschlossene Planungsphase oder noch keine Planungsphase. */
    nurLesen: boolean;
    /** Warum nur lesend — steht als Hinweis ueber der Liste. */
    sperrgrund: string | null;
    /** Es gibt noch keine Planungsphase (Grundlagen nie gespeichert). */
    ohnePhase: boolean;
    /**
     * Versand per Mail gesperrt — mit Grund (Uebungsprojekt: keine Mail an
     * Externe). PDF und Checkliste bleiben nutzbar; der Grund geht als
     * `sperrgrund` in den Versandauftrag, `versende` verweigert dann zentral.
     */
    versandGesperrt?: string | null;
    /**
     * Aenderung einreihen. `false` = darf nicht; sonst eine Quittung, deren
     * `bestaetigt` die DATENBANKbestaetigung abwartet — der Stempel nach einem
     * Versand meldet erst dann Erfolg (`useUnterlagen.ts`).
     */
    onAendern: (fn: (alt: PlanungUnterlage[]) => PlanungUnterlage[], sofort: boolean) => AenderungsQuittung | false;
    onErneutSpeichern: () => void;
    onHinweis: (text: string | null) => void;
    onZuGrundlagen: () => void;
}

const ADRESSAT_WERTE: FrageAdressat[] = ['BH', 'OP', 'TWP', 'TGA', 'FP', 'GUT', 'BEH', 'AN', 'BET'];

const STATUS_FARBE: Record<UnterlagenStatus, string> = {
    offen: 'bg-white text-slate-700 border-slate-300',
    angefordert: 'bg-blue-50 text-blue-700 border-blue-200',
    liegt_vor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    nicht_erforderlich: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_PUNKT: Record<UnterlagenStatus, string> = {
    offen: 'bg-slate-300',
    angefordert: 'bg-blue-500',
    liegt_vor: 'bg-emerald-500',
    nicht_erforderlich: 'bg-slate-200',
};

const FILTER: { key: UnterlagenFilter; label: string; titel: string }[] = [
    { key: 'relevant', label: 'Relevant', titel: 'Erforderlich, zu klären, im SiGe-Plan genannt oder schon bearbeitet' },
    { key: 'offen', label: 'Offen', titel: 'Relevant und weder vorliegend noch als nicht erforderlich begründet' },
    { key: 'alle', label: 'Alle', titel: 'Der ganze Katalog samt eigenen Unterlagen' },
];

const jetztIso = () => new Date().toISOString();

/** Kennung fuer eigene Eintraege. Zufall gehoert in den Aufrufer, nicht in
 *  die pure Funktion. */
const neueKennung = (): string => {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const lieferantText = (l: FrageAdressat[]): string =>
    l.length ? l.map(x => LIEFERANT_TITEL[x] || x).join(', ') : 'nicht festgelegt';

// ─── Kleinteile ─────────────────────────────────────────────────────────────

const Speicheranzeige: React.FC<{ stand: Speicherstand; nurLesen: boolean }> = ({ stand, nurLesen }) => {
    if (nurLesen) {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 shrink-0">
                <Lock className="w-3.5 h-3.5" /> Nur lesend
            </span>
        );
    }
    if (stand === 'fehler') {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-rose-600 shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" /> Nicht gespeichert
            </span>
        );
    }
    if (stand === 'speichert' || stand === 'wartet') {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-500 shrink-0">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Speichert …
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Gespeichert
        </span>
    );
};

const Merkmal: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
    <div className="min-w-0">
        <dt className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-0.5">{titel}</dt>
        <dd className="text-xs text-slate-700 leading-snug">{children}</dd>
    </div>
);

// ─── Eine Zeile ─────────────────────────────────────────────────────────────

const ZeileAnsicht: React.FC<{
    zeile: UnterlagenZeile;
    nurLesen: boolean;
    belege: ProjektDokument[];
    /** Stand der Projektablage — nur `bereit` erlaubt Verknuepfen und Abgleich. */
    ablage: 'laedt' | 'fehler' | 'bereit';
    alleDokumente: ProjektDokument[];
    /** Heutiger Tag in Ortszeit — Bezug fuer Fristvorgabe und „ueberfaellig". */
    heuteIso: string;
    onUebernehmen: (e: PlanungUnterlage, sofort: boolean) => boolean;
    onEntfernen: (id: string) => void;
    onHinweis: (text: string | null) => void;
}> = ({ zeile, nurLesen, belege, ablage, alleDokumente, heuteIso, onUebernehmen, onEntfernen, onHinweis }) => {
    const ablageGeladen = ablage === 'bereit';
    const [offen, setOffen] = useState(false);
    /** Entwurf der Begruendung, solange „nicht erforderlich" gewaehlt, aber noch nicht festgehalten ist. */
    const [begruendung, setBegruendung] = useState<string | null>(null);
    const [loeschenFragen, setLoeschenFragen] = useState(false);

    const d = zeile.dokument;
    const e = zeile.eintrag;
    const beleg = e && e.dokument_id ? belege.find(x => x.id === e.dokument_id) || null : null;
    const verwaist = ablageGeladen && belegVerwaist(e, alleDokumente);
    const gruende = relevanzGruende(zeile);
    const fristText = unterlagenFristText(e, heuteIso);
    const fristUeberfaellig = unterlagenFristUeberfaellig(e, heuteIso);
    /** Die Frist laesst sich pflegen, solange die Unterlage unerledigt ist und
     *  angefordert wurde oder schon eine Frist traegt. */
    const fristPflegbar = (zeile.status === 'angefordert' || !!(e && e.frist_am))
        && (zeile.status === 'offen' || zeile.status === 'angefordert');

    const statusWaehlen = (neu: UnterlagenStatus) => {
        if (neu === zeile.status) return;
        if (neu === 'nicht_erforderlich') {
            // Erst begruenden, dann festhalten — eine unbegruendete Ausbuchung
            // waere im Haftungsfall wertlos.
            setBegruendung(e ? e.bemerkung : '');
            setOffen(true);
            return;
        }
        // „Angefordert" bringt die Vorgabefrist mit (heute + 14 Tage, Ortszeit);
        // im aufgeklappten Teil laesst sie sich aendern.
        const r = setzeUnterlagenStatus(eintragFuerZeile(zeile), neu, {
            jetztIso: jetztIso(),
            fristAm: neu === 'angefordert' ? vorgabeUnterlagenFrist(heuteLokalIso()) : undefined,
        });
        if (r.status === 'fehler') { onHinweis(r.fehler); return; }
        onUebernehmen(r.wert, true);
    };

    const fristAendern = (wert: string) => {
        const r = setzeUnterlagenFrist(eintragFuerZeile(zeile), wert || null);
        if (r.status === 'fehler') { onHinweis(r.fehler); return; }
        onUebernehmen(r.wert, true);
    };

    const begruendungFesthalten = () => {
        const r = setzeUnterlagenStatus(eintragFuerZeile(zeile), 'nicht_erforderlich', {
            jetztIso: jetztIso(),
            begruendung: begruendung || '',
        });
        if (r.status === 'fehler') { onHinweis(r.fehler); return; }
        if (onUebernehmen(r.wert, true)) setBegruendung(null);
    };

    const bemerkungAendern = (text: string) => {
        const r = setzeUnterlagenBemerkung(eintragFuerZeile(zeile), text);
        if (r.status === 'fehler') { onHinweis(r.fehler); return; }
        onUebernehmen(r.wert, false);
    };

    const belegAendern = (dokumentId: string) => {
        onUebernehmen(verknuepfeUnterlagenDokument(eintragFuerZeile(zeile), dokumentId || null, jetztIso()), true);
    };

    const lieferantAendern = (wert: string) => {
        onUebernehmen({ ...eintragFuerZeile(zeile), lieferant: wert ? [wert as FrageAdressat] : [] }, true);
    };

    const ansehen = (dok: ProjektDokument) => {
        dokumentAnsehen(dok).catch(err => onHinweis(`Der Beleg konnte nicht geöffnet werden: ${describeError(err)}`));
    };

    const darfLoeschen = zeile.quelle === 'eigen' && !!e && e.status === 'offen' && !e.angefordert_am && !e.dokument_id;

    return (
        <div className={`border-b border-slate-100 last:border-0 ${zeile.status === 'nicht_erforderlich' ? 'bg-slate-50/60' : ''}`}>
            <div className="flex items-start gap-3 px-4 py-3">
                <span className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${STATUS_PUNKT[zeile.status]}`} />

                <button
                    type="button"
                    onClick={() => setOffen(o => !o)}
                    className="flex-1 text-left min-w-0"
                    aria-expanded={offen}
                >
                    <div className={`text-sm font-bold leading-snug ${zeile.status === 'nicht_erforderlich' ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-800'}`}>
                        {zeile.name}
                        {d && d.kurz && <span className="ml-1.5 text-[11px] font-mono font-bold text-slate-400 no-underline">({d.kurz})</span>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {zeile.relevanz === 'erforderlich' && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">erforderlich</span>
                        )}
                        {zeile.relevanz === 'zu_klaeren' && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">zu klären</span>
                        )}
                        {zeile.sigeSchritte.length > 0 && (
                            <span
                                title={zeile.sigeSchritte.join(' · ')}
                                className="text-[10px] font-black px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200"
                            >
                                im SiGe-Plan ({zeile.sigeSchritte.length})
                            </span>
                        )}
                        {d && (d.art === 'A' || d.art === 'B') && (
                            <span title={ART_TITEL[d.art]} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                {d.art} · {ART_KURZ[d.art]}
                            </span>
                        )}
                        {d && (
                            <span className={`text-[10px] font-bold ${d.beleg === 'MK' ? 'text-slate-500' : 'text-slate-400 italic'}`}>
                                {BELEG_LABEL[d.beleg]}
                            </span>
                        )}
                        {zeile.quelle === 'eigen' && <span className="text-[10px] font-bold text-indigo-500">eigene Unterlage</span>}
                        {zeile.gruppe === 'archiv' && <span className="text-[10px] font-bold text-slate-400">nicht mehr im Katalog</span>}
                        {e && e.angefordert_am && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600">
                                <Clock className="w-3 h-3" /> angefordert {datumDeutsch(e.angefordert_am)}
                            </span>
                        )}
                        {fristText && (
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] font-black px-1.5 py-0.5 rounded border ${
                                    fristUeberfaellig
                                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                                        : 'bg-blue-50 text-blue-700 border-blue-200'
                                }`}
                            >
                                {fristUeberfaellig ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                {fristText}
                            </span>
                        )}
                        {beleg && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                                <Link2 className="w-3 h-3" /> Beleg in der Ablage
                            </span>
                        )}
                        {verwaist && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600">
                                <AlertTriangle className="w-3 h-3" /> Beleg nicht verfügbar
                            </span>
                        )}
                    </div>
                    {!offen && e && e.bemerkung && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-1">{e.bemerkung}</div>
                    )}
                </button>

                <div className="shrink-0 flex items-center gap-2">
                    <select
                        value={begruendung !== null ? 'nicht_erforderlich' : zeile.status}
                        disabled={nurLesen}
                        onChange={ev => statusWaehlen(ev.target.value as UnterlagenStatus)}
                        aria-label={`Status von ${zeile.name}`}
                        className={`px-2 py-1 text-xs font-bold rounded-lg border disabled:opacity-60 ${STATUS_FARBE[zeile.status]}`}
                    >
                        {UNTERLAGEN_STATUS_WERTE.map(s => <option key={s} value={s}>{UNTERLAGEN_STATUS_LABEL[s]}</option>)}
                    </select>
                    <button type="button" onClick={() => setOffen(o => !o)} className="p-0.5 text-slate-400" aria-label="Einzelheiten">
                        {offen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {offen && (
                <div className="px-4 pb-4 pl-9 space-y-4">

                    {/* ─── Begruendung fuer „nicht erforderlich" ─────────── */}
                    {begruendung !== null && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                            <label className="block text-[11px] font-black text-amber-800 uppercase tracking-wider mb-1">
                                Begründung: Warum ist die Unterlage nicht erforderlich?
                            </label>
                            <textarea
                                value={begruendung}
                                onChange={ev => setBegruendung(ev.target.value)}
                                rows={2}
                                autoFocus
                                className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white"
                                placeholder="z. B. Kein Bestand vorhanden, Neubau auf unbebautem Grundstück."
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={begruendungFesthalten}
                                    disabled={!begruendung || !begruendung.trim()}
                                    className="px-3 py-1.5 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                                >
                                    Als nicht erforderlich festhalten
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setBegruendung(null)}
                                    className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ─── Was ist das, wann, wer ────────────────────────── */}
                    {d ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            <Merkmal titel="Wann benötigt">{d.wannBenoetigt || 'keine Angabe'}</Merkmal>
                            <Merkmal titel="Für den SiGeKo relevant">{d.sigekoRelevanz || 'keine Angabe'}</Merkmal>
                            <Merkmal titel="Vorlauf / Frist">{d.vorlauf || 'keine feste Frist'}</Merkmal>
                            <Merkmal titel="Wer liefert">{lieferantText(zeile.lieferant)}</Merkmal>
                            <Merkmal titel="Art">{d.art} · {ART_TITEL[d.art]}</Merkmal>
                            <Merkmal titel="Kennzeichen">
                                {d.beleg === 'MK'
                                    ? 'Belegt (Mängelkatalog), mit Rechtsgrundlage nachgewiesen.'
                                    : 'Fachpraxis: vor der Anforderung fachlich prüfen.'}
                            </Merkmal>
                            {(d.rechtsgrundlage || []).length > 0 && (
                                <div className="sm:col-span-2 flex items-start gap-2 text-[11px] text-slate-500">
                                    <Scale className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>{(d.rechtsgrundlage || []).join(' · ')}</span>
                                </div>
                            )}
                        </dl>
                    ) : (
                        <p className="text-xs text-slate-500 leading-snug">
                            {zeile.quelle === 'eigen' && 'Eigene Unterlage, nicht aus dem Katalog.'}
                            {zeile.quelle === 'sigeplan' && 'Steht im SiGe-Plan, ist aber kein Katalogdokument. Erklärungen gibt es dafür nicht.'}
                            {zeile.gruppe === 'archiv' && 'Der Katalog führt dieses Dokument nicht mehr. Der Eintrag bleibt mit seinem Stand in der Akte.'}
                        </p>
                    )}

                    {gruende.length > 0 && (
                        <ul className="space-y-1">
                            {gruende.map(g => (
                                <li key={g} className="flex items-start gap-2 text-[11px] text-slate-600">
                                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                                    <span>{g}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* ─── Bearbeiten ───────────────────────────────────── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                {zeile.status === 'nicht_erforderlich' ? 'Begründung' : 'Bemerkung'}
                            </span>
                            <textarea
                                value={e ? e.bemerkung : ''}
                                disabled={nurLesen}
                                onChange={ev => bemerkungAendern(ev.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                placeholder="Bei wem angefordert, Stand, Rückmeldung …"
                            />
                        </label>
                        <div className="space-y-3">
                            <label className="block">
                                <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                    Beleg aus der Projektablage
                                </span>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={e && e.dokument_id ? e.dokument_id : ''}
                                        disabled={nurLesen || !ablageGeladen}
                                        onChange={ev => belegAendern(ev.target.value)}
                                        className="w-full min-w-0 px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                    >
                                        <option value="">
                                            {ablage === 'bereit' ? '(kein Beleg verknüpft)' :ablage === 'laedt' ? 'Ablage wird geladen …' : 'Ablage nicht lesbar'}
                                        </option>
                                        {e && e.dokument_id && !beleg && (
                                            <option value={e.dokument_id}>(nicht verfügbar)</option>
                                        )}
                                        {belege.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {b.titel || b.dateiname}{b.module.includes('planungsphase') ? ' · Planungsphase' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {beleg && (
                                        <button
                                            type="button"
                                            onClick={() => ansehen(beleg)}
                                            className="shrink-0 inline-flex items-center gap-1 px-2 py-2 text-xs font-bold text-blue-600 hover:text-blue-700"
                                            title="Beleg ansehen"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <span className="block text-[11px] text-slate-400 mt-1">
                                    {verwaist
                                        ? 'Das verknüpfte Dokument ist in der Ablage nicht mehr verfügbar (gelöscht oder nur für Admins). Bitte neu verknüpfen.'
                                        : 'Ein verknüpftes Dokument setzt den Stand auf „liegt vor". Admin-gesperrte Dokumente taugen nicht als Beleg.'}
                                </span>
                            </label>
                            {fristPflegbar && (
                                <label className="block">
                                    <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                        Rückmeldung bis
                                    </span>
                                    <input
                                        type="date"
                                        value={e && e.frist_am ? e.frist_am : ''}
                                        disabled={nurLesen}
                                        onChange={ev => fristAendern(ev.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg text-sm disabled:bg-slate-50 ${
                                            fristUeberfaellig ? 'border-rose-300 text-rose-700' : 'border-slate-200'
                                        }`}
                                    />
                                    <span className="block text-[11px] text-slate-400 mt-1">
                                        {fristUeberfaellig
                                            ? 'Die Frist ist verstrichen, ohne dass die Unterlage vorliegt. Erinnern oder Frist verlängern.'
                                            : 'Vorgabe beim Anfordern: heute + 14 Tage. Nur Anzeige; die Erinnerung folgt später.'}
                                    </span>
                                </label>
                            )}
                            {zeile.quelle !== 'katalog' && (
                                <label className="block">
                                    <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Wer liefert</span>
                                    <select
                                        value={zeile.lieferant[0] || ''}
                                        disabled={nurLesen}
                                        onChange={ev => lieferantAendern(ev.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                    >
                                        <option value="">(nicht festgelegt)</option>
                                        {ADRESSAT_WERTE.map(a => <option key={a} value={a}>{ADRESSAT_LABEL[a]}</option>)}
                                    </select>
                                </label>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-[11px] text-slate-400">
                            {e && e.angefordert_am
                                ? <>Zuerst angefordert am {datumDeutsch(e.angefordert_am)}</>
                                : <>Noch nicht angefordert</>}
                            {e && e.status_am && e.status !== 'offen' && (
                                <> · {UNTERLAGEN_STATUS_LABEL[e.status]} seit {datumDeutsch(e.status_am)}</>
                            )}
                        </div>
                        {darfLoeschen && !nurLesen && (
                            loeschenFragen ? (
                                <span className="inline-flex items-center gap-2 text-[11px]">
                                    <span className="text-slate-500">Wirklich löschen?</span>
                                    <button type="button" onClick={() => onEntfernen(zeile.id)} className="font-bold text-rose-600 hover:text-rose-700">Ja, löschen</button>
                                    <button type="button" onClick={() => setLoeschenFragen(false)} className="font-bold text-slate-500 hover:text-slate-700">Nein</button>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setLoeschenFragen(true)}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 hover:text-rose-700"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Eintrag löschen
                                </button>
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Die Checkliste ─────────────────────────────────────────────────────────

/**
 * Reiter „Unterlagen" der Planungsphase: welche mitgeltenden Unterlagen
 * (Gutachten, Plaene, Nachweise, Anzeigen) fuer dieses Vorhaben vorliegen
 * muessen — und wie weit sie sind.
 *
 * Die Regeln stehen in `unterlagenLogic.ts`, Zustand und Speichern in
 * `useUnterlagen.ts`. Hier steht nur die Darstellung.
 */
export const UnterlagenCheckliste: React.FC<Props> = ({
    projectId, phaseId, bearbeiter, projektName, projektNummer, projektAdresse, pdfKontext,
    merkmale, merkmaleUngespeichert, eintraege, speicherstand, speicherfehler, hinweis,
    nurLesen, sperrgrund, ohnePhase, versandGesperrt, onAendern, onErneutSpeichern, onHinweis, onZuGrundlagen,
}) => {
    const gespeichertesProjekt = Boolean(projectId) && projectId !== 'temp-id';
    const sige = useSigePlanDokumente(gespeichertesProjekt ? projectId : null);
    const ablage = useDokumente(gespeichertesProjekt ? projectId : null);

    const [filter, setFilter] = useState<UnterlagenFilter>('relevant');
    const [suche, setSuche] = useState('');
    const [zugeklappt, setZugeklappt] = useState<Set<string>>(new Set());
    const [eigene, setEigene] = useState<{ name: string; lieferant: string } | null>(null);
    /** Zuletzt erzeugte Anforderung — Grundlage fuer „als angefordert festhalten". */
    const [letzteAnforderung, setLetzteAnforderung] = useState<{ lieferant: FrageAdressat; ids: string[] } | null>(null);
    /** Offener Versand-Dialog (Plan 4.1). `ids` = die Unterlagen auf dem Blatt. */
    const [versand, setVersand] = useState<{
        lieferant: FrageAdressat;
        ids: string[];
        vorbelegung: EmpfaengerVorbelegung;
        laeuft: boolean;
        fehler: string | null;
    } | null>(null);
    /** Die Zuordnung wird fuer den Dialog gelesen — Knopf solange gesperrt. */
    const [versandLaedt, setVersandLaedt] = useState<FrageAdressat | null>(null);
    /**
     * Mail raus, Stempel gescheitert. Bleibt stehen, bis „Stempel nachholen"
     * durchgeht — eine verschickte Anforderung ohne Datum in der Akte darf
     * nicht still verschwinden. `jetzt`/`frist` sind die Werte vom Versand:
     * das Anforderungsdatum ist der Mailzeitpunkt, nicht der Nachhol-Klick.
     */
    const [stempelOffen, setStempelOffen] = useState<{
        lieferant: FrageAdressat;
        ids: string[];
        empfaenger: string;
        jetzt: string;
        frist: string;
        hinweis: string | null;
    } | null>(null);
    /** „Stempel nachholen" wartet gerade auf die Datenbank. */
    const [nachholLaeuft, setNachholLaeuft] = useState(false);
    /** Ruecklauf: Vorschlag, der gerade bestaetigt wird (Beleg → „liegt vor"). */
    const [belegBestaetigung, setBelegBestaetigung] = useState<BelegVorschlag | null>(null);
    /** Ruecklauf-Vorschlaege, die der Mensch fuer diese Sitzung weggeklickt hat. */
    const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(new Set());
    const [meldung, setMeldung] = useState<string | null>(null);
    const [handbuchLaeuft, setHandbuchLaeuft] = useState(false);
    /** Eingelesenes Handbuch, solange die Vorschau offen ist. */
    const [einlesen, setEinlesen] = useState<{
        gelesen: HandbuchGelesen;
        vorschlaege: NotizVorschlag[];
        /** Zaehlt neu gerechnete Vorschauen — der Dialog startet dann mit frischer Auswahl. */
        runde: number;
        /** Wie viele Eintraege seit der letzten Vorschau in der App geaendert wurden. */
        erneut: number;
    } | null>(null);
    const dateiWahl = useRef<HTMLInputElement>(null);

    const sigeSchritte = sige.daten ? sige.daten.schritte : null;
    // Der Tag in Ortszeit ist Bezug fuer Fristvorgabe und „ueberfaellig" —
    // nicht `toISOString()`, der liefert nachts den Vortag. Je Darstellung neu
    // bestimmt (billig), damit ein ueber Mitternacht offenes Modul nicht mit
    // dem Vortag rechnet; die Vorgabefrist beim Anfordern liest ihn frisch.
    const heuteIso = heuteLokalIso();
    const zeilen = useMemo(
        () => baueUnterlagenZeilen({ merkmale, eintraege, sigeSchritte: sigeSchritte || [] }),
        [merkmale, eintraege, sigeSchritte],
    );
    const fortschritt = useMemo(() => berechneUnterlagenFortschritt(zeilen), [zeilen]);
    const ueberfaellig = useMemo(() => ueberfaelligeUnterlagen(zeilen, heuteIso), [zeilen, heuteIso]);
    const zahlen = useMemo(() => ({
        relevant: filtereUnterlagen(zeilen, 'relevant').length,
        offen: filtereUnterlagen(zeilen, 'offen').length,
        alle: zeilen.length,
    }), [zeilen]);
    const gefiltert = useMemo(() => filtereUnterlagen(zeilen, filter, suche), [zeilen, filter, suche]);
    const gruppen = useMemo(() => gruppiereUnterlagen(gefiltert), [gefiltert]);
    const artC = useMemo(() => artCDokumente(), []);
    const lieferanten = useMemo(() => lieferantenUebersicht(zeilen), [zeilen]);
    const belege = useMemo(() => belegfaehigeDokumente(ablage.dokumente), [ablage.dokumente]);
    const ablageZustand: 'laedt' | 'fehler' | 'bereit' = ablage.laden ? 'laedt' : ablage.fehler ? 'fehler' : 'bereit';
    // Ruecklauf (Plan 4.3): hochgeladene Dokumente der Ablage, die eine
    // Unterlage belegen koennten. Nur Vorschlaege — verknuepft wird nach
    // Bestaetigung. Weggeklickte bleiben fuer diese Sitzung weg.
    const rueckflussBelege = useMemo(
        () => (ablageZustand === 'bereit' ? rueckflussVorschlaege(ablage.dokumente, zeilen, []).belege : [])
            .filter(v => !ausgeblendet.has(`${v.dokument.id}|${v.zeileId}`)),
        [ablageZustand, ablage.dokumente, zeilen, ausgeblendet],
    );

    /** Eingereiht oder nicht — die Zeilen brauchen keine Datenbankbestaetigung. */
    const uebernehmen = (e: PlanungUnterlage, sofort: boolean): boolean =>
        !!onAendern(alt => uebernehmeUnterlage(alt, e), sofort);

    // ─── Handbuch ───────────────────────────────────────────────────────
    // Erst erzeugen, wenn SiGe-Plan und Ablage gelesen sind — sonst fehlten
    // „im SiGe-Plan"-Marken und Belegtitel auf dem Blatt.
    const handbuchBereit = !!phaseId && !sige.laden && ablageZustand !== 'laedt' && !handbuchLaeuft;

    /** Erlaubt auch im Lesemodus und bei abgeschlossener Phase — es aendert nichts. */
    const handbuchErzeugen = async () => {
        if (!phaseId || handbuchLaeuft) return;
        setHandbuchLaeuft(true);
        try {
            const standIso = new Date().toISOString();
            const modell = baueHandbuchModell({
                zeilen: gefiltert, alleZeilen: zeilen, filter, suche, standIso, phaseId, projectId,
                projektName, projektNummer, projektAdresse,
                bauherr: sige.daten && sige.daten.bauherr ? sige.daten.bauherr : '',
                aufgestelltVon: bearbeiter || '',
                belegTitel: new Map(belege.map(b => [b.id, b.titel || b.dateiname])),
                belegeBekannt: ablageZustand === 'bereit',
            });
            const gebaut = await baueHandbuchPdf(modell, pdfKontext);
            if (gebaut.status === 'fehler') { onHinweis(gebaut.fehler); return; }
            const gespeichert = speichereHandbuch(gebaut.wert, handbuchDateiname(projektNummer, projektName, standIso));
            if (gespeichert.status === 'fehler') { onHinweis(gespeichert.fehler); return; }
            setMeldung(`Handbuch mit ${gefiltert.length} Unterlage${gefiltert.length === 1 ? '' : 'n'} erzeugt. `
                + 'Am Rechner ausgefüllte Notizen lassen sich über „Notizen einlesen" übernehmen.');
        } finally {
            setHandbuchLaeuft(false);
        }
    };

    const dateiGewaehlt = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const datei = ev.target.files && ev.target.files[0];
        // Zuruecksetzen, damit dieselbe Datei nach einer Korrektur erneut gewaehlt werden kann.
        if (dateiWahl.current) dateiWahl.current.value = '';
        if (!datei) return;
        if (nurLesen) {
            onHinweis(sperrgrund || 'Im Lesemodus lassen sich keine Notizen einlesen.');
            return;
        }
        // Groesse VOR dem Lesen: pdf-lib laeuft ohne Worker im Hauptfaden.
        const zuGross = pruefeHandbuchDatei(datei.size);
        if (zuGross) { onHinweis(zuGross); return; }
        try {
            const gelesen = await liesHandbuchPdf(new Uint8Array(await datei.arrayBuffer()));
            const fehler = pruefeHandbuch(gelesen, { phaseId, projectId });
            if (fehler) { onHinweis(fehler); return; }
            onHinweis(null);
            setEinlesen({ gelesen, vorschlaege: vergleicheHandbuchNotizen(gelesen, zeilen), runde: 0, erneut: 0 });
        } catch (e) {
            onHinweis(e instanceof Error ? e.message : describeError(e));
        }
    };

    /**
     * Alle gewaehlten Notizen in EINEM Schreibvorgang.
     *
     * Geprueft wird IM Schreib-Callback gegen den dann aktuellen Stand: Hat
     * sich eine betroffene Bemerkung seit der Vorschau geaendert, schreibt
     * der Callback nichts, und die Vorschau erscheint neu — mit den
     * betroffenen Eintraegen als Konflikt zum erneuten Bestaetigen.
     */
    const notizenUebernehmen = (auswahl: Record<string, NotizAktion>) => {
        if (!einlesen) return;
        const stand = einlesen.gelesen.meta ? einlesen.gelesen.meta.stand : '';
        // Der Callback laeuft synchron in `onAendern`; hier haelt er fest, was er sah.
        const lauf: { ergebnis: ReturnType<typeof uebernehmeHandbuchNotizen> | null; basis: PlanungUnterlage[] } = {
            ergebnis: null,
            basis: [],
        };
        const ok = onAendern(alt => {
            const r = uebernehmeHandbuchNotizen(alt, zeilen, einlesen.vorschlaege, auswahl, stand);
            lauf.ergebnis = r;
            lauf.basis = alt;
            return r.uebernommen ? r.liste : alt;
        }, true);
        const ergebnis = lauf.ergebnis;
        if (!ok || !ergebnis) return;
        if (ergebnis.veraltet.length) {
            setEinlesen({
                gelesen: einlesen.gelesen,
                vorschlaege: vergleicheErneut(einlesen.gelesen, zeilen, lauf.basis, ergebnis.veraltet),
                runde: einlesen.runde + 1,
                erneut: ergebnis.veraltet.length,
            });
            return;
        }
        setEinlesen(null);
        setMeldung(`${ergebnis.uebernommen} Notiz${ergebnis.uebernommen === 1 ? '' : 'en'} aus dem Handbuch übernommen.`
            + (ergebnis.abgelehnt.length ? ` Nicht übernommen: ${ergebnis.abgelehnt.join(', ')}.` : ''));
    };

    const entfernen = (id: string) => {
        const pruefung = entferneEigeneUnterlage(eintraege, id);
        if (pruefung.status === 'fehler') { onHinweis(pruefung.fehler); return; }
        onAendern(alt => {
            const r = entferneEigeneUnterlage(alt, id);
            return r.status === 'ok' ? r.wert : alt;
        }, true);
    };

    const eigeneAnlegen = () => {
        if (!eigene) return;
        const r = neueEigeneUnterlage(
            eigene.name,
            eigene.lieferant ? [eigene.lieferant as FrageAdressat] : [],
            neueKennung(),
            eintraege,
        );
        if (r.status === 'fehler') { onHinweis(r.fehler); return; }
        if (uebernehmen(r.wert, true)) setEigene(null);
    };

    /** Anforderungsblatt an EINEN Lieferanten. Auch im Lesemodus erlaubt —
     *  es aendert nichts, wie der Export der Klaerungsliste. */
    const anforderungErzeugen = (lieferant: FrageAdressat) => {
        const liste = unterlagenFuerLieferant(zeilen, lieferant);
        // Ortszeit: der UTC-Tag waere kurz nach Mitternacht der Vortag —
        // auf dem Blatt und im Dateinamen.
        const heute = heuteLokalIso();
        const empfaenger = ADRESSAT_LABEL[lieferant] || LIEFERANT_TITEL[lieferant] || lieferant;
        const gebaut = baueUnterlagenAnforderungPdf(liste, pdfKontext, {
            projektName, projektNummer, bauvorhabenAdresse: projektAdresse,
            empfaengerName: empfaenger,
            datumIso: heute,
        });
        if (gebaut.status === 'fehler') { onHinweis(gebaut.fehler); return; }
        const gespeichert = speicherePdf(gebaut.wert, anforderungDateiname(projektName || 'Projekt', LIEFERANT_TITEL[lieferant] || lieferant, heute));
        if (gespeichert.status === 'fehler') { onHinweis(gespeichert.fehler); return; }
        setMeldung(null);
        setLetzteAnforderung({ lieferant, ids: liste.map(z => z.id) });
    };

    // ─── Versand (Plan 4.1) ─────────────────────────────────────────────
    // Erst der Mail-Erfolg stempelt „angefordert" (`fuehreAnforderungVersandAus`).
    // Der Dialog oeffnet erst, wenn die Empfaenger-Zuordnung gelesen ist —
    // so entsteht sein Zustand einmal aus den Props, ohne Nachladen.

    const versandOeffnen = async (lieferant: FrageAdressat) => {
        if (!phaseId || nurLesen || versandLaedt) return;
        if (versandGesperrt) { onHinweis(versandGesperrt); return; }
        const ids = unterlagenFuerLieferant(zeilen, lieferant).map(z => z.id);
        if (!ids.length) { onHinweis('Für diesen Empfänger ist keine Unterlage offen.'); return; }
        setVersandLaedt(lieferant);
        let vorbelegung: EmpfaengerVorbelegung;
        try {
            const lesung = await fetchEmpfaenger(phaseId);
            const e = empfaengerVorbelegung(lesung.zuordnung, lieferant);
            vorbelegung = { ...e, stand: e.name ? 'gefunden' : 'leer', hinweis: lesung.hinweis };
        } catch (e) {
            vorbelegung = {
                name: '', email: '', stand: 'leer',
                hinweis: `Die Empfänger-Zuordnung konnte nicht gelesen werden: ${describeError(e)}`,
            };
        } finally {
            setVersandLaedt(null);
        }
        setMeldung(null);
        setVersand({ lieferant, ids, vorbelegung, laeuft: false, fehler: null });
    };

    /**
     * Stempelt die Unterlagen des Blatts als angefordert — gegen den
     * juengsten Stand (`alt`), wie `alsAngefordertFesthalten` — und wartet
     * auf die DATENBANKbestaetigung: „eingereiht" ist kein Nachweis.
     * `gesperrt` = die Checkliste durfte nicht schreiben (Lesemodus, Phase
     * weg); `nicht_gespeichert` = eingereiht, aber das Schreiben scheiterte
     * (der Stand bleibt im Schreiber vorgemerkt, „Stempel nachholen" stoesst
     * ihn erneut an).
     */
    const stempeln = async (ids: string[], jetzt: string, frist: string): Promise<StempelErgebnis> => {
        const betroffen = zeilen.filter(z => ids.includes(z.id));
        let gestempelt = 0;
        const quittung = onAendern(alt => {
            const r = markiereAlsAngefordert(alt, betroffen, jetzt, frist);
            gestempelt = r.gestempelt;
            return r.gestempelt ? r.liste : alt;
        }, true);
        if (!quittung) {
            return { status: 'gesperrt', grund: sperrgrund || 'Die Checkliste ist gerade nicht beschreibbar (Lesemodus oder keine Planungsphase).' };
        }
        try {
            await quittung.bestaetigt;
            return { status: 'gestempelt', gestempelt };
        } catch (e) {
            return { status: 'nicht_gespeichert', grund: unterlagenSpeicherFehler(describeError(e)) };
        }
    };

    const anforderungVersenden = async (eingabe: VersandEingabe) => {
        if (!versand || !phaseId || versand.laeuft) return;
        if (versandGesperrt) { setVersand({ ...versand, fehler: versandGesperrt }); return; }
        const { lieferant, ids } = versand;
        const liste = zeilen.filter(z => ids.includes(z.id));
        if (!liste.length) {
            setVersand({ ...versand, fehler: 'Die Unterlagen dieser Anforderung sind inzwischen erledigt, es gibt nichts zu versenden.' });
            return;
        }
        // Ortszeit fuer Blatt und Dateinamen; `jetzt` ist der Stempel.
        const heute = heuteLokalIso();
        const jetzt = jetztIso();
        const frist = vorgabeUnterlagenFrist(heute);
        const gebaut = baueUnterlagenAnforderungPdf(liste, pdfKontext, {
            projektName, projektNummer, bauvorhabenAdresse: projektAdresse,
            // Name und Rolle im Anschriftfeld — das Blatt geht an eine Person,
            // nicht an eine Rolle.
            empfaengerName: `${eingabe.name}, ${LIEFERANT_TITEL[lieferant] || ADRESSAT_LABEL[lieferant] || lieferant}`,
            datumIso: heute,
        });
        if (gebaut.status === 'fehler') { setVersand({ ...versand, fehler: gebaut.fehler }); return; }
        const blob = pdfAlsBlob(gebaut.wert);
        if (blob.status === 'fehler') { setVersand({ ...versand, fehler: blob.fehler }); return; }

        setVersand({ ...versand, laeuft: true, fehler: null });
        const empfaengerText = `${eingabe.name} <${eingabe.email}>`;
        const ergebnis = await fuehreAnforderungVersandAus({
            versende: () => versende({
                art: 'unterlagenanforderung',
                projectId,
                planungId: phaseId,
                projektName,
                empfaenger: [{ name: eingabe.name, email: eingabe.email }],
                betreff: eingabe.betreff,
                text: eingabe.text,
                pdf: blob.wert,
                dateiname: anforderungDateiname(projektName || 'Projekt', LIEFERANT_TITEL[lieferant] || lieferant, heute),
                titel: `Unterlagenanforderung an ${ADRESSAT_LABEL[lieferant] || lieferant} vom ${datumDeutsch(heute)}`,
                fassungRef: anforderungFassungRef(lieferant, jetzt),
                stand: `Stand ${datumDeutsch(heute)}`,
                // Der zentrale Riegel: aus einem Uebungsprojekt verweigert `versende`.
                sperrgrund: versandGesperrt || null,
            }),
            // Gestempelt wird der Zeitpunkt der Mail (`versendetAm`), nicht der Klick.
            stempeln: versendetAm => stempeln(ids, versendetAm, frist),
            jetztIso: jetzt,
        });

        if (ergebnis.status === 'fehler') {
            // Mail nicht raus, nichts gestempelt — der Dialog bleibt offen.
            setVersand(v => (v ? { ...v, laeuft: false, fehler: ergebnis.fehler } : v));
            return;
        }
        setVersand(null);
        setLetzteAnforderung(null);
        // Nicht-fatale Hinweise (Ablage gescheitert, Sendebericht fehlt) und
        // nicht erreichte Empfaenger gehoeren in die Meldung — nie verschluckt.
        const nachsatz = (ergebnis.fehler ? ` Nicht alle Empfänger wurden erreicht: ${ergebnis.fehler}.` : '')
            + (ergebnis.hinweise ? ` Hinweis: ${ergebnis.hinweise}` : '');
        if (ergebnis.status === 'stempel_offen') {
            setStempelOffen({
                lieferant, ids, empfaenger: empfaengerText, jetzt: ergebnis.versendetAm, frist,
                hinweis: `${ergebnis.grund}${nachsatz}`.trim() || null,
            });
            return;
        }
        setStempelOffen(null);
        setMeldung(
            `Anforderung an ${empfaengerText} versendet (${liste.length} Unterlage${liste.length === 1 ? '' : 'n'}, Blatt in der Ablage). `
            + (ergebnis.gestempelt
                ? `${ergebnis.gestempelt} als angefordert festgehalten (${datumDeutsch(ergebnis.versendetAm)}), Rückmeldung bis ${datumDeutsch(frist)}.`
                    + (ergebnis.gestempelt < liste.length
                        ? ' Die übrigen waren schon angefordert; dort bleibt das erste Anforderungsdatum stehen.'
                        : '')
                : 'Alle Unterlagen waren schon angefordert; das erste Anforderungsdatum bleibt stehen.')
            + nachsatz,
        );
    };

    const stempelNachholen = async () => {
        if (!stempelOffen || nachholLaeuft) return;
        setNachholLaeuft(true);
        // Nach einem Schreibfehler liegt der Stempel schon im vorgemerkten
        // Stand des Schreibers — dann reiht `stempeln` nichts Neues ein, und
        // erst „Erneut speichern" bringt den Stand hinaus. Beides anzustossen
        // ist unschaedlich: der Schreiber laeuft nie doppelt.
        onErneutSpeichern();
        const s = await stempeln(stempelOffen.ids, stempelOffen.jetzt, stempelOffen.frist);
        setNachholLaeuft(false);
        if (s.status !== 'gestempelt') {
            setStempelOffen(alt => (alt ? { ...alt, hinweis: s.grund } : alt));
            return;
        }
        setStempelOffen(null);
        setMeldung(`Stempel nachgeholt: ${s.gestempelt} Unterlage(n) als angefordert festgehalten (${datumDeutsch(stempelOffen.jetzt)}), `
            + `Rückmeldung bis ${datumDeutsch(stempelOffen.frist)}.`);
    };

    // ─── Ruecklauf (Plan 4.3) ───────────────────────────────────────────

    /** Beleg verknuepfen UND „liegt vor" setzen — beides im selben, bestaetigten Klick. */
    const belegZuordnen = (v: BelegVorschlag) => {
        const jetzt = jetztIso();
        const ok = onAendern(alt => {
            const z = zeilen.find(x => x.id === v.zeileId);
            if (!z) return alt;
            const basis = alt.find(e => e.id === v.zeileId) || eintragFuerZeile({ ...z, eintrag: null });
            return uebernehmeUnterlage(alt, verknuepfeUnterlagenDokument(basis, v.dokument.id, jetzt));
        }, true);
        if (!ok) return;
        setBelegBestaetigung(null);
        setMeldung(`„${v.dokument.titel}" als Beleg für „${v.unterlage}" verknüpft. Die Unterlage gilt als „liegt vor".`);
    };

    const vorschlagAusblenden = (v: BelegVorschlag) => {
        setAusgeblendet(alt => new Set(alt).add(`${v.dokument.id}|${v.zeileId}`));
        if (belegBestaetigung && belegBestaetigung.dokument.id === v.dokument.id && belegBestaetigung.zeileId === v.zeileId) {
            setBelegBestaetigung(null);
        }
    };

    const dokumentOeffnen = (id: string) => {
        const dok = ablage.dokumente.find(d => d.id === id);
        if (!dok) { onHinweis('Das Dokument ist in der Ablage nicht mehr verfügbar.'); return; }
        dokumentAnsehen(dok).catch(err => onHinweis(`Das Dokument konnte nicht geöffnet werden: ${describeError(err)}`));
    };

    const alsAngefordertFesthalten = () => {
        if (!letzteAnforderung) return;
        const betroffen = zeilen.filter(z => letzteAnforderung.ids.includes(z.id));
        const jetzt = jetztIso();
        const frist = vorgabeUnterlagenFrist(heuteLokalIso());
        // Die Zahl fuer die Meldung aus dem gezeichneten Stand; geschrieben
        // wird gegen den juengsten (`alt`), damit nichts Zwischenzeitliches
        // ueberschrieben wird.
        const vorschau = markiereAlsAngefordert(eintraege, betroffen, jetzt, frist);
        const ok = onAendern(alt => {
            const r = markiereAlsAngefordert(alt, betroffen, jetzt, frist);
            return r.gestempelt ? r.liste : alt;
        }, true);
        if (!ok) return;
        setLetzteAnforderung(null);
        setMeldung(vorschau.gestempelt
            ? `${vorschau.gestempelt} Unterlage(n) als angefordert festgehalten (${datumDeutsch(jetzt)}), Rückmeldung bis ${datumDeutsch(frist)}.`
                + (vorschau.gestempelt < betroffen.length
                    ? ' Die übrigen waren schon angefordert oder sind inzwischen erledigt; dort bleibt der Stand samt erstem Anforderungsdatum unverändert.'
                    : '')
            : 'Alle Unterlagen dieser Anforderung waren schon angefordert; das erste Datum bleibt stehen.');
    };

    const gruppeUmschalten = (g: string) => {
        setZugeklappt(alt => {
            const neu = new Set(alt);
            if (neu.has(g)) neu.delete(g); else neu.add(g);
            return neu;
        });
    };

    const quote = fortschritt.relevant > 0 ? Math.round((fortschritt.liegtVor / fortschritt.relevant) * 100) : 0;
    const erforderlichOffen = fortschritt.erforderlichOffen;

    return (
        <div className="space-y-4">

            {/* ─── Kopf: Fortschritt ─────────────────────────────────── */}
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                        <h3 className="font-bold text-slate-800">Mitgeltende Unterlagen</h3>
                        <p className="text-xs text-slate-500 leading-snug">
                            Gutachten, Pläne, Nachweise und Anzeigen, die vor der Ausführung vorliegen müssen,
                            abgeleitet aus Ihren Projektmerkmalen und dem SiGe-Plan.
                        </p>
                    </div>
                    <Speicheranzeige stand={speicherstand} nurLesen={nurLesen} />
                </div>

                <div className="text-sm font-bold text-slate-800">{fortschrittText(fortschritt)}.</div>
                <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${quote}%` }} />
                </div>
                {fortschritt.nichtErforderlich > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1">
                        {fortschritt.nichtErforderlich} als nicht erforderlich begründet (nicht mitgezählt).
                    </div>
                )}

                {erforderlichOffen.length > 0 && (
                    <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-900 leading-snug">
                            <strong>{erforderlichOffen.length} erforderliche Unterlage{erforderlichOffen.length === 1 ? ' ist' : 'n sind'} noch nicht angefordert:</strong>{' '}
                            {erforderlichOffen.slice(0, 6).map(z => z.name).join(', ')}
                            {erforderlichOffen.length > 6 && ` und ${erforderlichOffen.length - 6} weitere`}.
                        </span>
                    </div>
                )}
                {ueberfaellig.length > 0 && (
                    <div className="mt-3 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                        <Clock className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <span className="text-xs text-rose-900 leading-snug">
                            <strong>Rückmeldefrist verstrichen bei {ueberfaellig.length} Unterlage{ueberfaellig.length === 1 ? '' : 'n'}:</strong>{' '}
                            {ueberfaellig.slice(0, 6).map(z => z.name).join(', ')}
                            {ueberfaellig.length > 6 && ` und ${ueberfaellig.length - 6} weitere`}.
                        </span>
                    </div>
                )}

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                    {sige.laden ? (
                        <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> SiGe-Plan wird gelesen …</span>
                    ) : sige.daten && sige.daten.lesefehler ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Der SiGe-Plan konnte nicht gelesen werden, deshalb fehlt die Kennzeichnung „im SiGe-Plan". ({sige.daten.lesefehler})
                        </span>
                    ) : sige.daten && sige.daten.planId ? (
                        <span>
                            SiGe-Plan gelesen: {sige.daten.schritte.length} Arbeitsschritt{sige.daten.schritte.length === 1 ? '' : 'e'} mit mitgeltenden Dokumenten.
                        </span>
                    ) : (
                        <span>Zu diesem Projekt gibt es noch keinen SiGe-Plan.</span>
                    )}
                    <button
                        type="button"
                        onClick={() => { void sige.neuLaden(); }}
                        className="inline-flex items-center gap-1 font-bold text-blue-600 hover:text-blue-700"
                    >
                        <RefreshCw className="w-3 h-3" /> neu lesen
                    </button>
                </div>
                {merkmaleUngespeichert && (
                    <div className="mt-2 text-[11px] text-amber-700">
                        Die Relevanz folgt Ihren noch nicht gespeicherten Angaben in den Grundlagen.
                    </div>
                )}
            </section>

            {/* ─── Meldungen ─────────────────────────────────────────── */}
            {sperrgrund && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2 flex-wrap">
                    <Lock className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                    <span className="flex-1 min-w-0 whitespace-pre-line">{sperrgrund}</span>
                    {ohnePhase && (
                        <button type="button" onClick={onZuGrundlagen} className="text-xs font-bold underline shrink-0">
                            Zu den Grundlagen
                        </button>
                    )}
                </div>
            )}
            {speicherfehler && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2 flex-wrap">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1 min-w-0">{speicherfehler}</span>
                    <button
                        type="button"
                        onClick={onErneutSpeichern}
                        disabled={speicherstand === 'speichert'}
                        className="text-xs font-bold underline shrink-0 disabled:opacity-40"
                    >
                        Erneut speichern
                    </button>
                </div>
            )}
            {hinweis && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                    <span className="flex-1 min-w-0 whitespace-pre-line">{hinweis}</span>
                    <button type="button" onClick={() => onHinweis(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                </div>
            )}

            {meldung && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1 min-w-0">{meldung}</span>
                    <button type="button" onClick={() => setMeldung(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                </div>
            )}

            {/* ─── Anforderung je Lieferant ──────────────────────────── */}
            {lieferanten.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Anforderung je Lieferant</span>
                        {lieferanten.map(l => (
                            <span key={l.lieferant} className="inline-flex rounded-lg border border-blue-200 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => anforderungErzeugen(l.lieferant)}
                                    title={`Anforderungsblatt (PDF) an: ${ADRESSAT_LABEL[l.lieferant] || l.lieferant}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100"
                                >
                                    <Download className="w-3.5 h-3.5" /> {l.lieferant} ({l.offen})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { void versandOeffnen(l.lieferant); }}
                                    disabled={nurLesen || !phaseId || !!versandLaedt || !!versandGesperrt}
                                    title={versandGesperrt
                                        ? versandGesperrt
                                        : nurLesen
                                            ? 'Im Lesemodus und bei abgeschlossener Planungsphase wird nicht versendet.'
                                            : `Per E-Mail versenden an: ${ADRESSAT_LABEL[l.lieferant] || l.lieferant}. Die Unterlagen gelten erst nach erfolgreichem Versand als angefordert.`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 border-l border-blue-200"
                                >
                                    {versandLaedt === l.lieferant ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Versenden …
                                </button>
                            </span>
                        ))}
                    </div>
                    <p className="text-[11px] text-slate-400">
                        PDF: Blatt herunterladen und selbst verschicken, danach „Als angefordert festhalten". Versenden: Mail mit
                        Blatt im Anhang an den zugeordneten Empfänger, Blatt in der Ablage. Erst der Mail-Erfolg stempelt „angefordert".
                        {versandGesperrt && <span className="text-amber-700"> {versandGesperrt}</span>}
                    </p>
                </div>
            )}
            {stempelOffen && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-900 flex items-center gap-3 flex-wrap">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
                    <span className="flex-1 min-w-0">
                        Die Anforderung an {stempelOffen.empfaenger} ist <strong>versendet</strong> ({stempelOffen.ids.length} Unterlage{stempelOffen.ids.length === 1 ? '' : 'n'},
                        {' '}{datumDeutsch(stempelOffen.jetzt)}), aber der Stempel „angefordert" konnte nicht gesetzt werden.
                        {stempelOffen.hinweis ? ` ${stempelOffen.hinweis}` : ''} Bitte nachholen, sonst steht die Anforderung ohne Datum in der Akte.
                    </span>
                    <button
                        type="button"
                        onClick={() => { void stempelNachholen(); }}
                        disabled={nurLesen || nachholLaeuft}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-rose-600 rounded-lg hover:bg-rose-700 disabled:opacity-40"
                    >
                        {nachholLaeuft && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Stempel nachholen
                    </button>
                </div>
            )}
            {letzteAnforderung && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-900 flex items-center gap-3 flex-wrap">
                    <Send className="w-4 h-4 shrink-0 text-blue-600" />
                    <span className="flex-1 min-w-0">
                        Anforderung an {ADRESSAT_LABEL[letzteAnforderung.lieferant] || letzteAnforderung.lieferant} erzeugt
                        ({letzteAnforderung.ids.length} Unterlage{letzteAnforderung.ids.length === 1 ? '' : 'n'}).
                        Wenn Sie das Blatt verschickt haben, halten Sie es hier fest. Das Datum der ersten Anforderung bleibt danach stehen.
                    </span>
                    <button
                        type="button"
                        onClick={alsAngefordertFesthalten}
                        disabled={nurLesen}
                        className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                    >
                        Als angefordert festhalten
                    </button>
                    <button type="button" onClick={() => setLetzteAnforderung(null)} className="text-xs font-bold underline">
                        Nicht verschickt
                    </button>
                </div>
            )}

            {/* ─── Ruecklauf aus der Ablage (Plan 4.3) ──────────────── */}
            {rueckflussBelege.length > 0 && (
                <div className="bg-white rounded-2xl border border-indigo-200 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 bg-indigo-50/60 border-b border-indigo-100">
                        <Inbox className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span className="font-bold text-slate-800 text-sm flex-1 min-w-0">Rücklauf aus der Projektablage</span>
                        <span className="text-xs text-slate-400 shrink-0">{rueckflussBelege.length} Vorschl{rueckflussBelege.length === 1 ? 'ag' : 'äge'}</span>
                    </div>
                    <p className="px-4 py-2 text-[11px] text-slate-500 italic bg-slate-50/50 border-b border-slate-100">
                        Hochgeladene Dokumente, deren Titel eine offene Unterlage nennt. Ein Vorschlag ist nur ein Vorschlag;
                        verknüpft wird erst nach Ihrer Bestätigung, und dann gilt die Unterlage als „liegt vor".
                    </p>
                    {rueckflussBelege.map(v => {
                        const schluessel = `${v.dokument.id}|${v.zeileId}`;
                        const fragt = !!belegBestaetigung
                            && belegBestaetigung.dokument.id === v.dokument.id
                            && belegBestaetigung.zeileId === v.zeileId;
                        return (
                            <div key={schluessel} className="px-4 py-3 border-b border-slate-100 last:border-0">
                                <div className="flex items-start gap-3 flex-wrap">
                                    <Link2 className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-800">
                                            <button
                                                type="button"
                                                onClick={() => dokumentOeffnen(v.dokument.id)}
                                                className="font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 max-w-full"
                                                title="Dokument ansehen"
                                            >
                                                <span className="truncate">{v.dokument.titel || v.dokument.dateiname}</span>
                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                            </button>
                                            {' '}→ Beleg für <strong>{v.unterlage}</strong>
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-0.5">{v.grund}</div>
                                    </div>
                                    {!fragt && (
                                        <div className="shrink-0 flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setBelegBestaetigung(v)}
                                                disabled={nurLesen}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-40"
                                            >
                                                <Link2 className="w-3.5 h-3.5" /> Beleg zuordnen …
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => vorschlagAusblenden(v)}
                                                className="text-xs font-bold text-slate-500 hover:text-slate-700"
                                                title="Vorschlag für diese Sitzung ausblenden"
                                            >
                                                Nicht zuordnen
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {fragt && (
                                    <div className="mt-2 ml-7 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-3 flex-wrap">
                                        <span className="text-xs text-emerald-900 flex-1 min-w-0">
                                            „{v.dokument.titel || v.dokument.dateiname}" als Beleg für „{v.unterlage}" verknüpfen?
                                            Die Unterlage wird damit auf <strong>„liegt vor"</strong> gesetzt.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => belegZuordnen(v)}
                                            className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                                        >
                                            Verknüpfen (liegt vor)
                                        </button>
                                        <button type="button" onClick={() => setBelegBestaetigung(null)} className="text-xs font-bold text-slate-600 hover:text-slate-800">
                                            Abbrechen
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ─── Filter ────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
                <div data-glass="umschalter" className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                    {FILTER.map(f => (
                        <button
                            key={f.key}
                            type="button"
                            title={f.titel}
                            onClick={() => setFilter(f.key)}
                            className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                                filter === f.key ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {f.label} <span className="opacity-70">({zahlen[f.key]})</span>
                        </button>
                    ))}
                </div>
                <label className="relative flex-1 min-w-[12rem]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="search"
                        value={suche}
                        onChange={ev => setSuche(ev.target.value)}
                        placeholder="Suchen in Name, Kurzform, Bemerkung …"
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                </label>
                <button
                    type="button"
                    onClick={() => setEigene(eigene ? null : { name: '', lieferant: '' })}
                    disabled={nurLesen}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                >
                    <Plus className="w-3.5 h-3.5" /> Eigene Unterlage
                </button>
                <div className="w-full flex items-center gap-2 flex-wrap pt-3 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => { void handbuchErzeugen(); }}
                        disabled={!handbuchBereit}
                        title={phaseId
                            ? 'PDF der aktuellen Ansicht mit ausfüllbaren Notizfeldern'
                            : 'Das Handbuch gibt es erst, wenn die Grundlagen gespeichert sind.'}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-40"
                    >
                        {handbuchLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />} Handbuch
                    </button>
                    <button
                        type="button"
                        onClick={() => { if (dateiWahl.current) dateiWahl.current.click(); }}
                        disabled={nurLesen || !phaseId}
                        title={nurLesen
                            ? 'Im Lesemodus und bei abgeschlossener Planungsphase lassen sich keine Notizen einlesen.'
                            : 'Am Rechner ausgefülltes Handbuch wählen; die Notizen werden vor dem Übernehmen angezeigt.'}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                    >
                        <Upload className="w-3.5 h-3.5" /> Notizen einlesen
                    </button>
                    <input
                        ref={dateiWahl}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={ev => { void dateiGewaehlt(ev); }}
                    />
                    <span className="text-[11px] text-slate-400 flex-1 min-w-[14rem]">
                        Das Handbuch zeigt die aktuelle Ansicht mit einem Notizfeld je Unterlage. Am Rechner ausgefüllte
                        Notizen lassen sich wieder einlesen, handschriftliche Notizen auf Papier nicht.
                    </span>
                </div>
            </div>

            {eigene && !nurLesen && (
                <div className="bg-white rounded-2xl border border-blue-200 p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="block sm:col-span-2">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Bezeichnung</span>
                            <input
                                type="text"
                                value={eigene.name}
                                autoFocus
                                onChange={ev => setEigene({ ...eigene, name: ev.target.value })}
                                onKeyDown={ev => { if (ev.key === 'Enter') eigeneAnlegen(); }}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                placeholder="z. B. Statik Hilfsunterstützung Decke über EG"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Wer liefert</span>
                            <select
                                value={eigene.lieferant}
                                onChange={ev => setEigene({ ...eigene, lieferant: ev.target.value })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                            >
                                <option value="">(nicht festgelegt)</option>
                                {ADRESSAT_WERTE.map(a => <option key={a} value={a}>{ADRESSAT_LABEL[a]}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={eigeneAnlegen}
                            disabled={!eigene.name.trim()}
                            className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                        >
                            Hinzufügen
                        </button>
                        <button type="button" onClick={() => setEigene(null)} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800">
                            Abbrechen
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Liste ─────────────────────────────────────────────── */}
            {zahlen.alle === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                    <p className="text-sm text-slate-500">
                        Der Katalog mitgeltender Unterlagen ist noch leer. Eigene Unterlagen lassen sich trotzdem erfassen.
                    </p>
                </div>
            )}
            {zahlen.alle > 0 && gruppen.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                    <p className="text-sm text-slate-500">
                        {filter === 'offen' && !suche ? 'Keine relevante Unterlage ist mehr offen.' : 'Keine Unterlage passt zu diesem Filter.'}
                    </p>
                </div>
            )}

            {gruppen.map(g => {
                const zu = zugeklappt.has(g.gruppe);
                const offenErforderlich = g.zeilen.filter(z => z.erforderlich && z.status === 'offen').length;
                return (
                    <div key={g.gruppe} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => gruppeUmschalten(g.gruppe)}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-left hover:bg-slate-100 transition-colors"
                        >
                            {zu ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            <span className="font-bold text-slate-800 text-sm flex-1 min-w-0">{g.titel}</span>
                            {offenErforderlich > 0 && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-black text-rose-600 shrink-0" title="Erforderlich und noch nicht angefordert">
                                    <AlertTriangle className="w-3.5 h-3.5" /> {offenErforderlich}
                                </span>
                            )}
                            <span className="text-xs text-slate-400 shrink-0">{g.zeilen.length}</span>
                        </button>
                        {!zu && g.zeilen.map(z => (
                            <ZeileAnsicht
                                key={z.id}
                                zeile={z}
                                nurLesen={nurLesen}
                                belege={belege}
                                ablage={ablageZustand}
                                alleDokumente={ablage.dokumente}
                                heuteIso={heuteIso}
                                onUebernehmen={uebernehmen}
                                onEntfernen={entfernen}
                                onHinweis={onHinweis}
                            />
                        ))}
                    </div>
                );
            })}

            {/* ─── Art C: einmal als Grundsatz, nie je Zeile ─────────── */}
            <section className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
                <h4 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
                    Allgemeine Pflichten des Arbeitgebers
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">{GRUNDSATZ_ART_C}</p>
                <p className="text-[11px] text-slate-400 mt-2">
                    Diese Unterlagen hält jeder Arbeitgeber selbst vor. Sie stehen deshalb nicht einzeln in der
                    Checkliste und werden nicht angefordert.
                </p>
                {artC.length > 0 && (
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                        {artC.map(d => (
                            <li key={d.id} title={d.wannBenoetigt} className="text-xs text-slate-600">
                                · {d.name}{d.kurz ? ` (${d.kurz})` : ''}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {versand && phaseId && (
                <AnforderungVersandDialog
                    key={`${versand.lieferant}|${versand.ids.join(',')}`}
                    lieferant={versand.lieferant}
                    zeilen={zeilen.filter(z => versand.ids.includes(z.id))}
                    projektName={projektName}
                    projektNummer={projektNummer}
                    fristIso={vorgabeUnterlagenFrist(heuteIso)}
                    absender={bearbeiter}
                    vorbelegung={versand.vorbelegung}
                    laeuft={versand.laeuft}
                    fehler={versand.fehler}
                    onVersenden={eingabe => { void anforderungVersenden(eingabe); }}
                    onAbbrechen={() => { if (!versand.laeuft) setVersand(null); }}
                />
            )}

            {einlesen && (
                <HandbuchImportDialog
                    key={einlesen.runde}
                    erneut={einlesen.erneut}
                    vorschlaege={einlesen.vorschlaege}
                    standIso={einlesen.gelesen.meta ? einlesen.gelesen.meta.stand : ''}
                    unbekannteFelder={einlesen.gelesen.unbekannteFelder.length}
                    gesperrt={nurLesen ? (sperrgrund || 'Im Lesemodus lassen sich keine Notizen übernehmen.') : null}
                    onUebernehmen={notizenUebernehmen}
                    onAbbrechen={() => setEinlesen(null)}
                />
            )}
        </div>
    );
};
